/**
 * Custodian Connections (Sprint C14 Lane B WP-8)
 *
 * FI-tenant-scoped configuration view for the custodian integrations
 * Aaim uses to issue pledge contracts (Coinbase Prime / Coinbase Custody
 * for digital assets). The actual credential management is performed
 * centrally by the Aaim team; this page is the FI's read-only view of:
 *
 *   - which custodians Aaim supports and the asset classes each one holds
 *   - whether each custodian's integration is enabled, READ from the same
 *     data surface as the Custodian Status page
 *   - the request-change path for credential changes
 *
 * The deeper connection-health and last-sync surface is the Custodian Status
 * page (CustodianStatus.tsx, /monitoring/custodian-status).
 *
 * ENABLEMENT IS READ, NEVER DECLARED. The Enabled / Disabled badge comes from
 * the CUSTODY integration row returned by GET /integrations/third-party, the
 * read CustodianStatus.tsx makes. Until that read succeeds, or when it returns
 * no row for a custodian, the card renders no badge and says where the status
 * lives. A constant here once declared Coinbase "Enabled" while the platform
 * row read `enabled: false` (UI run 2026-09-27, fi-g4-5).
 *
 * NOTE: there is no FI-tenant-facing custodian configuration backend
 * today. Coinbase webhook subscriptions are managed at the platform
 * level, and the integration row read here is the platform record for the
 * custodian rather than a per-tenant setting. Per `.claude/rules/ui-package.md`
 * Save Button Discipline: rather than show a Save button that does nothing,
 * we surface a clear "request changes" path.
 *
 * ENABLEMENT IS PLATFORM-WIDE, AND THE PAGE SAYS SO. GET /integrations/third-party
 * (platform-admin.openapi.yaml, integrationService.listThirdParty) takes no
 * tenant, so the badge is Aaim's status for the custodian across every
 * institution. Operator ruling 2026-09-27 (custodian-enablement-scope): keep the
 * platform row and state its scope on the page. A per-institution enablement
 * needs a contract field or endpoint that no spec declares (the GAP is GH #2958),
 * so the page has none to read. CustodianStatus.tsx carries the same sentence.
 */
import { MessageSquarePlus, Shield, ShieldCheck } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import { Badge, Button, Card, Heading } from '@aaimplatform/ui';
import { requestChangeHref } from '../../utils/request-change';
import { useThirdPartyIntegrations } from '../../hooks/useIntegrations';
import type { ThirdPartyIntegration } from '../../api/services/integrations';

interface CustodianMetadata {
  id: 'coinbase';
  name: string;
  /** Matches `Integration.vendor` on the CUSTODY row that carries this custodian's status. */
  vendor: string;
  description: string;
  /**
   * Display labels for the asset classes this custodian holds, worded as the
   * terminology register's operatorConfig context
   * (.claude/manifests/terminology/terms/collateral.yaml, asset-class).
   */
  assetClassLabels: string[];
}

const CUSTODIANS: ReadonlyArray<CustodianMetadata> = [
  {
    id: 'coinbase',
    name: 'Coinbase Prime / Custody',
    vendor: 'Coinbase',
    description:
      'Digital asset custody and pledge-contract issuance for member crypto holdings.',
    assetClassLabels: ['Major Cryptocurrency', 'Stablecoins', 'Other Digital Assets'],
  },
  // NOTE: Fi-Tek is NOT a custodian -- it is the Fi-Tek GWES (Global WealthES)
  // trust/wealth enterprise-service-bus integration (schema-registry/
  // fitek-gwes-adapter.openapi.yaml), not an asset-custody provider. It does not
  // belong in the custodian-connections list and was removed here. If the Fi-Tek
  // trust integration needs an operator surface, it belongs on a trust/brokerage
  // integrations page, not this one.
];

const CUSTODIAN_STATUS_PATH = '/monitoring/custodian-status';

/** The scope of the status shown, worded identically on Custodian Status. */
const PLATFORM_WIDE_STATUS_NOTE =
  'The status shown is Aaim’s platform-wide integration status for each custodian and applies to every institution; custodians have no per-institution enablement.';

/**
 * The CUSTODY integration row for a custodian, or null when the read has not
 * succeeded or returned no such row. Classification is by category, the same
 * rule CustodianStatus.tsx applies (GH #1959); the vendor match only picks the
 * row among custodians.
 */
export function findCustodianIntegration(
  integrations: ThirdPartyIntegration[] | undefined,
  custodian: Pick<CustodianMetadata, 'vendor'>,
): ThirdPartyIntegration | null {
  if (!integrations) return null;
  return (
    integrations.find(
      (row) => row.category === 'CUSTODY' && row.vendor === custodian.vendor,
    ) ?? null
  );
}

function EnablementBadge({
  row,
  isLoading,
}: {
  row: ThirdPartyIntegration | null;
  isLoading: boolean;
}) {
  if (row) {
    return row.enabled ? (
      <Badge variant="success" size="small">Enabled</Badge>
    ) : (
      <Badge variant="neutral" size="small">Disabled</Badge>
    );
  }
  return (
    <span className="text-xs font-sans text-tertiary leading-normal flex-shrink-0">
      {isLoading ? 'Checking status...' : 'Status unavailable'}
    </span>
  );
}

export function CustodianConnectionsPage() {
  const navigate = useNavigate();
  const { data: integrations, isLoading } = useThirdPartyIntegrations();

  // GH #1962: this used to hand the operator off to a mail client. It now
  // files a real, tracked case, pre-scoped to the custodian they were looking at.
  const handleRequestChanges = (custodianName: string) => {
    void navigate(
      requestChangeHref({
        surface: 'Custodian connections',
        item: `${custodianName} connection or credential change`,
        detail: `Custodian: ${custodianName}`,
      }),
    );
  };

  return (
    <div>
      <div className="mb-6">
        {/* Page title lives in the sticky topnav (AppHeader); the body opens
            with the descriptive subtitle, no redundant heading. */}
        <p className="text-sm font-sans text-tertiary leading-normal max-w-2xl">
          Custodian integrations Aaim uses to issue pledge contracts for your
          institution. Credentials are rotated centrally so an in-flight pledge never
          fails mid-issuance. Use Request changes on any custodian below to
          enable it, rotate its credentials, or open a new connection. Each
          request files a tracked case, pre-scoped to that custodian.
        </p>
        {/* Source: GET /integrations/third-party, which is platform-wide
            (custodian-enablement-scope ruling). */}
        <p className="mt-2 text-sm font-sans text-tertiary leading-normal max-w-2xl">
          {PLATFORM_WIDE_STATUS_NOTE}
        </p>
      </div>

      <div className="space-y-4">
        {CUSTODIANS.map((custodian) => {
          const row = findCustodianIntegration(integrations, custodian);
          const isEnabled = row?.enabled === true;
          return (
            <Card key={custodian.id}>
              <div className="flex items-start justify-between gap-6 mb-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {isEnabled ? (
                    <ShieldCheck
                      className="w-5 h-5 text-growth flex-shrink-0 mt-0.5"
                      aria-hidden="true"
                    />
                  ) : (
                    <Shield
                      className="w-5 h-5 text-tertiary flex-shrink-0 mt-0.5"
                      aria-hidden="true"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    {/* The page title is AppHeader's h1, so the card title is
                        the next level down, styled as a card title (fi-g4-15). */}
                    <Heading level={2} as={3} className="mb-1">
                      {custodian.name}
                    </Heading>
                    <div className="text-sm font-sans text-tertiary leading-normal">
                      {custodian.description}
                    </div>
                  </div>
                </div>
                <EnablementBadge row={row} isLoading={isLoading} />
              </div>

              <dl className="mb-4 pt-4 border-t border-recessed">
                <dt className="text-xs font-sans font-semibold uppercase text-tertiary mb-1">
                  Asset classes
                </dt>
                <dd className="text-sm font-sans text-primary leading-normal">
                  {custodian.assetClassLabels.join(', ')}
                </dd>
              </dl>

              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-4 border-t border-recessed">
                <div className="text-xs font-sans text-tertiary leading-normal">
                  {row
                    ? 'For connection health and last-sync times, see '
                    : 'Aaim could not read this custodian’s status here. It is shown on '}
                  <Link
                    to={CUSTODIAN_STATUS_PATH}
                    className="text-accent hover:underline"
                  >
                    Custodian Status
                  </Link>
                  .
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => handleRequestChanges(custodian.name)}
                >
                  <MessageSquarePlus className="w-4 h-4" />
                  Request changes
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="bg-elevated border border-recessed rounded-sm p-4 mt-6">
        <div className="text-xs font-sans text-tertiary leading-normal">
          <strong>Why is this read-only?</strong> Custodian credentials are
          used to issue pledge contracts, and rotating one mid-flight fails the
          pledges riding on it. Aaim sequences the rotation with the custodian so
          that cannot happen. Every change still starts here: use Request changes
          on the custodian above and it opens a tracked case.
        </div>
      </div>
    </div>
  );
}
